import chalk from 'chalk';
import ora from 'ora';
import { requireTeam } from '../lib/auth-guard.js';
import { api, ApiError } from '../lib/api-client.js';
import { success, error, info, table } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface RecurringTemplate {
  id: string;
  title: string;
  kind: string;
  expression?: string;
  every_interval?: string;
  natural_language?: string;
  timezone: string;
  enabled: boolean;
  next_spawn_at?: string;
  last_spawned_at?: string;
  spawn_count: number;
  priority: number;
  created_at: string;
}

async function resolveAgentId(codeName: string): Promise<string | null> {
  try {
    const data = await api.get<{ agent_id: string }>(`/agents/${codeName}`);
    return data.agent_id;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

function priorityLabel(p: number): string {
  return p === 1 ? chalk.red('HIGH') : p === 3 ? chalk.dim('LOW') : chalk.yellow('MED');
}

function formatSpawnTime(isoDate: string | undefined, timezone: string): string {
  if (!isoDate) return chalk.dim('—');
  try {
    return new Intl.DateTimeFormat('en-AU', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: timezone,
    }).format(new Date(isoDate));
  } catch {
    return new Date(isoDate).toLocaleString();
  }
}

// ---------------------------------------------------------------------------
// agt kanban recurring add
// ---------------------------------------------------------------------------

export async function kanbanRecurringAddCommand(
  title: string,
  opts: {
    agent: string;
    every: string;
    priority?: string;
    description?: string;
    estimate?: string;
    deliverable?: string;
    timezone?: string;
  },
): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;
  const json = isJsonMode();

  // Validate numeric inputs
  let priority: number | undefined;
  if (opts.priority) {
    priority = Number(opts.priority);
    if (!Number.isInteger(priority) || priority < 1 || priority > 3) {
      if (json) {
        jsonOutput({ ok: false, error: 'Priority must be 1, 2, or 3' });
      } else {
        error('Priority must be 1 (high), 2 (medium), or 3 (low)');
      }
      process.exitCode = 1;
      return;
    }
  }

  let estimatedMinutes: number | undefined;
  if (opts.estimate) {
    estimatedMinutes = Number(opts.estimate);
    if (!Number.isInteger(estimatedMinutes) || estimatedMinutes <= 0) {
      if (json) {
        jsonOutput({ ok: false, error: 'Estimate must be a positive integer (minutes)' });
      } else {
        error('Estimate must be a positive integer (minutes)');
      }
      process.exitCode = 1;
      return;
    }
  }

  const spinner = ora({ text: 'Creating recurring template…', isSilent: json });
  spinner.start();

  const agentId = await resolveAgentId(opts.agent);
  if (!agentId) {
    spinner.fail(`Agent "${opts.agent}" not found`);
    process.exitCode = 1;
    return;
  }

  try {
    const data = await api.post<{ template: RecurringTemplate }>('/host/kanban/recurring', {
      agent_id: agentId,
      title,
      description: opts.description ?? undefined,
      priority,
      estimated_minutes: estimatedMinutes,
      deliverable: opts.deliverable ?? undefined,
      schedule: opts.every,
      timezone: opts.timezone ?? undefined,
    });

    spinner.stop();

    if (json) {
      jsonOutput({ ok: true, template: data.template });
      return;
    }

    success(`Recurring task created: ${chalk.bold(title)}`);
    info(`Schedule:   ${chalk.cyan(data.template.natural_language ?? data.template.expression ?? data.template.every_interval ?? '')}`);
    info(`Next spawn: ${formatSpawnTime(data.template.next_spawn_at, data.template.timezone)}`);
    info(`Timezone:   ${data.template.timezone}`);
  } catch (err) {
    spinner.fail('Failed to create recurring template');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}

// ---------------------------------------------------------------------------
// agt kanban recurring list
// ---------------------------------------------------------------------------

export async function kanbanRecurringListCommand(
  opts: { agent: string },
): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;
  const json = isJsonMode();

  const spinner = ora({ text: 'Fetching recurring templates…', isSilent: json });
  spinner.start();

  const agentId = await resolveAgentId(opts.agent);
  if (!agentId) {
    spinner.fail(`Agent "${opts.agent}" not found`);
    process.exitCode = 1;
    return;
  }

  try {
    const data = await api.get<{ templates: RecurringTemplate[] }>(
      `/host/kanban/recurring?agent_id=${agentId}`,
    );

    spinner.stop();

    if (json) {
      jsonOutput({ ok: true, templates: data.templates });
      return;
    }

    if (data.templates.length === 0) {
      info('No recurring templates found.');
      return;
    }

    const rows = data.templates.map((t) => [
      t.id.slice(0, 8),
      t.title,
      t.natural_language ?? t.expression ?? t.every_interval ?? '—',
      priorityLabel(t.priority),
      t.enabled ? chalk.green('Active') : chalk.dim('Disabled'),
      formatSpawnTime(t.next_spawn_at, t.timezone),
      String(t.spawn_count),
    ]);

    table(
      ['ID', 'Title', 'Schedule', 'Priority', 'Status', 'Next Spawn', 'Spawned'],
      rows,
    );
  } catch (err) {
    spinner.fail('Failed to fetch recurring templates');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}

// ---------------------------------------------------------------------------
// agt kanban recurring disable
// ---------------------------------------------------------------------------

export async function kanbanRecurringDisableCommand(
  titleOrId: string,
  opts: { agent: string },
): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;
  const json = isJsonMode();

  const spinner = ora({ text: 'Disabling recurring template…', isSilent: json });
  spinner.start();

  const agentId = await resolveAgentId(opts.agent);
  if (!agentId) {
    spinner.fail(`Agent "${opts.agent}" not found`);
    process.exitCode = 1;
    return;
  }

  try {
    const data = await api.get<{ templates: RecurringTemplate[] }>(
      `/host/kanban/recurring?agent_id=${agentId}`,
    );

    const matches = data.templates.filter(
      (t) => t.id.startsWith(titleOrId) || t.title.toLowerCase() === titleOrId.toLowerCase(),
    );

    if (matches.length === 0) {
      spinner.fail(`Recurring template "${titleOrId}" not found`);
      process.exitCode = 1;
      return;
    }

    if (matches.length > 1) {
      spinner.fail(`Ambiguous match for "${titleOrId}" — ${matches.length} templates found:`);
      for (const m of matches) {
        info(`  ${m.id.slice(0, 8)} — ${m.title}`);
      }
      info('Use a longer ID prefix to disambiguate.');
      process.exitCode = 1;
      return;
    }

    const match = matches[0]!;
    await api.patch(`/host/kanban/recurring/${match.id}`, { enabled: false });
    spinner.stop();

    if (json) {
      jsonOutput({ ok: true, id: match.id, title: match.title, enabled: false });
    } else {
      success(`Disabled: ${chalk.bold(match.title)}`);
    }
  } catch (err) {
    spinner.fail('Failed to disable recurring template');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}
